import { acceptHMRUpdate, defineStore } from 'pinia'

import api from '@/lib/api'

import app from '@/app'
import { ApiProduct, Customization } from '@/types'

type State = {
  productsCache: Record<string, ApiProduct>
  customizationSnapshot: CustomizationSnapshot
  analyticsData: {
    navigatedFromPageTitle: string
  }
}
type CustomizationSnapshot = {
  customizations?: Customization[]
  slugPath?: string
  selectedSku?: string
  inventoryIds?: string[]
}

type Actions = {
  getProductData: (productToGet: {
    code: string
    salesToStock: number
    aliqPerLot: number
    volPerAliq: number
  }) => Promise<ApiProduct>
  setCustomizationSnapshot: (customization: CustomizationSnapshot) => void
  getCustomizationSnapshot: (slugPath: string) => CustomizationSnapshot | null
  unsetCustomizationSnapshot: () => void
  setProductAnalyticsData: (navigatedFromPageTitle: { navigatedFromPageTitle: string }) => void
  resetProductsCache: () => void
  resetState: () => void
}

export const useProductStore = defineStore<string, State, Record<string, never>, Actions>(
  'product',
  {
    state: () => ({
      productsCache: {},
      customizationSnapshot: {
        slugPath: '',
        customizations: [],
        selectedSku: '',
        inventoryIds: [],
      },
      analyticsData: {
        navigatedFromPageTitle: '',
      },
    }),

    actions: {
      async getProductData(productToGet) {
        try {
          const cacheKey = Object.entries({
            code: productToGet.code,
            salesToStock: productToGet.salesToStock,
            aliqPerLot: productToGet.aliqPerLot,
            volPerAliq: productToGet.volPerAliq,
          })
            .map(([key, value]) => `${key}:${value}`)
            .join('&')
          const isProductInCache = this.productsCache[cacheKey]
          if (isProductInCache) {
            return this.productsCache[cacheKey]
          } else {
            const { data: productDataRaw } = await api.products.getProduct(productToGet)
            this.productsCache = { ...this.productsCache, [cacheKey]: productDataRaw }
            return productDataRaw
          }
        } catch (error) {
          app.config.globalProperties.$err.sendMessage(
            `Get product request failed. Code: ${productToGet.code}. URL: ${window.location.href}`,
          )
          throw error
        }
      },

      setCustomizationSnapshot({ customizations, slugPath, selectedSku, inventoryIds }) {
        this.customizationSnapshot = {
          customizations,
          slugPath,
          selectedSku,
          inventoryIds,
        }
      },
      getCustomizationSnapshot(slugPath: string) {
        return this.customizationSnapshot.slugPath === slugPath ? this.customizationSnapshot : null
      },

      unsetCustomizationSnapshot() {
        this.customizationSnapshot = {
          customizations: [],
          slugPath: '',
          selectedSku: '',
          inventoryIds: [],
        }
      },

      setProductAnalyticsData({ navigatedFromPageTitle }) {
        this.analyticsData = { navigatedFromPageTitle }
      },

      resetProductsCache() {
        this.productsCache = {}
      },

      resetState() {
        this.$reset()
      },
    },
  },
)

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useProductStore, import.meta.hot))
}
